<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('workPlanning.title') }}</h2>

    <div class="form-block work-planning-form">
      <ValidationObserver
        ref="workPlanningForm"
        v-slot="{ handleSubmit }"
      >
        <el-form
          class="operations-search-form"
          size="small"
          label-position="top"
        >
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="18">
              <el-form-item>
                <span slot="label">{{
                  $t('workPlanning.search.customerName')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerName"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'name', exact: false });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectCustomer"
                  @change="emptySelectedCustomer"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>

            <el-col :span="6">
              <el-form-item>
                <span slot="label">{{ $t('workPlanning.search.code') }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.code"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'id', exact: true });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectCustomer"
                  @change="emptySelectedCustomer"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="12">
              <FormItem :label="$t('workPlanning.dateFrom')">
                <el-date-picker
                  v-model="formData.from"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="12">
              <FormItem :label="$t('workPlanning.dateTo')">
                <el-date-picker
                  v-model="formData.to"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('workPlanning.analysisId')">
                <el-input
                  v-model="formData.analysisId"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>
          </el-row>
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('workPlanning.orderId')">
                <el-input
                  v-model="formData.orderId"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="6">
              <FormItem
                :rules="formData.orderId ? '' : 'required'"
                :label="$t('workPlanning.groupingId')"
              >
                <el-input
                  v-model="formData.groupingId"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="6">
              <FormItem :label="$t('workPlanning.sampleCode')">
                <el-input
                  v-model="formData.sampleCode"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('workPlanning.sampleCodeFrom')">
                <el-input
                  v-model="formData.sampleCodeFrom"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('workPlanning.sampleCodeTo')">
                <el-input
                  v-model="formData.sampleCodeTo"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="12">
              <FormItem
                rules="required"
                :label="$t('workPlanning.printType')"
              >
                <el-radio-group
                  v-model="formData.printType"
                  @change="updateStoredForm"
                >
                  <el-radio
                    class="work-planning-form__radio"
                    v-for="type in availablePrintTypes"
                    :key="type.value"
                    :label="type.value"
                    >{{ type.label }}</el-radio
                  >
                </el-radio-group>
                <p
                  v-if="formData.printType === 'stampafrazionata'"
                  class="el-form-item__error"
                >
                  {{ $t('workPlanning.bulkPrintWarning') }}
                </p>
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="12">
              <FormItem
                rules="required"
                :label="$t('workPlanning.printAction')"
              >
                <el-checkbox-group
                  v-model="formData.printAction"
                  @change="onPrintActionChange"
                >
                  <el-checkbox
                    class="work-planning-form__radio"
                    v-for="action in printActions"
                    :key="action.value"
                    :label="action.value"
                    >{{ action.label }}</el-checkbox
                  >
                </el-checkbox-group>
              </FormItem>
            </el-col>
          </el-row>

          <FormButtons>
            <el-button
              type="primary"
              native-type="submit"
              @click.prevent="handleSubmit(printLabels)"
            >
              {{ $t('workPlanning.print') }}
            </el-button>
          </FormButtons>
        </el-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
  import printJS from 'print-js';
  import moment from 'moment';
  import fileSaver from 'file-saver';
  import { mapGetters, mapActions } from 'vuex';
  import FormItem from '@/components/FormItem.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import { ANALYSES_COMMODITY_CATEGORIES } from '@/config/constants';
  import { customersApi, workPlanningApi } from '@/api';

  const initialFormData = {
    customerName: '',
    code: '',
    customerId: null,
    from: '',
    to: '',
    analysisId: '',
    orderId: '',
    groupingId: '',
    sampleCode: '',
    sampleCodeFrom: '',
    sampleCodeTo: '',
    printType: '',
    printAction: ['samplesLabels', 'workProgram'],
  };

  export default {
    name: 'WorkPlanning',
    components: {
      FormItem,
      FormButtons,
      AutocompleteItem,
    },
    data() {
      return {
        formData: { ...initialFormData },
        printActions: [
          {
            label: this.$t('workPlanning.samplesLabels'),
            value: 'samplesLabels',
          },
          { label: this.$t('workPlanning.labLabels'), value: 'labLabels' },
          {
            label: this.$t('workPlanning.printAttachment'),
            value: 'printAttachment',
          },
          { label: this.$t('workPlanning.workProgram'), value: 'workProgram' },
        ],
        analysisComodityCategories: [
          {
            label: this.$t('enums.comodityCategory.microbiological'),
            value: ANALYSES_COMMODITY_CATEGORIES.MICROBIOLOGICAL,
          },
          {
            label: this.$t('enums.comodityCategory.chemical'),
            value: ANALYSES_COMMODITY_CATEGORIES.CHEMICAL,
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['forms']),
      availablePrintTypes() {
        const base = [
          { label: this.$t('workPlanning.previewOnly'), value: 'previewOnly' },
          { label: this.$t('workPlanning.generatePdf'), value: 'generatePdf' },
          { label: this.$t('workPlanning.printPdf'), value: 'printPdf' },
        ];
        if (this.formData.printAction.includes('samplesLabels')) {
          base.push({
            label: this.$t('workPlanning.stampafrazionata'),
            value: 'stampafrazionata',
          });
        }
        return base;
      },
    },
    methods: {
      ...mapActions(['updateForm']),
      async searchCustomer({ text, cb, field, exact }) {
        if (text.length >= 3) {
          const response = await customersApi.autocompleteFetch(
            field,
            text,
            exact
          );
          const customers = response.data.map((c) => ({
            value: c.id,
            label: c[field],
            code: `${c.id}`,
            customerName: c.name,
          }));
          cb(customers);
        } else {
          cb([]);
        }
      },
      selectCustomer(item) {
        this.formData.customerId = item.value;
        this.formData.customerName = item.customerName;
        this.formData.code = item.code;
        this.updateForm({ formKey: 'workPlanning', formData: this.formData });
      },
      emptySelectedCustomer(text) {
        if (text === '') {
          this.formData.customerId = null;
          this.formData.customerName = '';
          this.formData.code = '';
          this.updateForm({ formKey: 'workPlanning', formData: this.formData });
        }
      },
      updateStoredForm() {
        this.updateForm({ formKey: 'workPlanning', formData: this.formData });
      },
      onPrintActionChange(newVal) {
        // NOTE: reset printType if samplesLabels is unchecked while stampafrazionata is active,
        // since that option is only available when samplesLabels is selected
        if (
          !newVal.includes('samplesLabels') &&
          this.formData.printType === 'stampafrazionata'
        ) {
          this.formData.printType = '';
        }
        this.updateStoredForm();
      },
      async printLabels() {
        const where = {};
        if (this.formData.customerId) {
          where.customerId = this.formData.customerId;
        }
        if (this.formData.from) {
          where.and = [
            { acceptedAt: { gt: moment.utc(this.formData.from).toDate() } },
          ];
        }
        if (this.formData.to) {
          if (where.and && where.and.length)
            where.and.push({
              acceptedAt: {
                lt: moment.utc(this.formData.to).endOf('day').toDate(),
              },
            });
          else
            where.and = [
              {
                acceptedAt: {
                  lt: moment.utc(this.formData.to).endOf('day').toDate(),
                },
              },
            ];
        }
        if (this.formData.orderId) {
          where.orderId = this.formData.orderId;
        }
        if (this.formData.groupingId) {
          where.groupingId = this.formData.groupingId;
        }
        if (this.formData.sampleCode) {
          where.labSampleCode = this.formData.sampleCode;
        } else if (this.formData.sampleCodeFrom && this.formData.sampleCodeTo) {
          where.labSampleCode = {
            between: [this.formData.sampleCodeFrom, this.formData.sampleCodeTo],
          };
        }
        const analysisCode = this.formData.analysisId;
        const print = this.formData.printType === 'printPdf';
        const isBulk =
          this.formData.printType === 'stampafrazionata' &&
          this.formData.printAction.includes('samplesLabels');

        // Bulk material-label printing is async AND non-blocking.
        // Progress is surfaced through its own persistent
        // notification.
        if (isBulk) {
          this.generateMaterialLabelsBulk({ where }).catch((error) =>
            this.throwError(error)
          );
        }

        const blobActions = this.formData.printAction.filter(
          (action) => !(isBulk && action === 'samplesLabels')
        );
        if (!blobActions.length) return;

        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });

        try {
          const responses = await Promise.all(
            blobActions.map(async (action) => {
              switch (action) {
                case 'samplesLabels':
                  return workPlanningApi.labelsPdf({
                    where,
                    analysisCode,
                    print,
                  });

                case 'labLabels':
                  return workPlanningApi.materialsPdf({
                    where,
                    analysisCode,
                    print,
                  });

                case 'workProgram':
                  return this.generateWorkProgram(
                    { where, analysisCode },
                    loading
                  );

                default:
                  return workPlanningApi.attachmentsZip({
                    where,
                    analysisCode,
                  });
              }
            })
          );
          loading.close();
          responses.forEach((response) => {
            this.viewOrSaveAttachment(response.data);
          });
        } catch (error) {
          this.throwError(error);
        } finally {
          loading.close();
        }
      },

      async generateMaterialLabelsBulk({ where }) {
        const {
          data: { jobId },
        } = await workPlanningApi.materialsPdfBulkEnqueue({
          where,
          print: true,
        });

        const notification = this.$notify({
          showClose: false,
          duration: 0,
          type: 'info',
          message: this.$t('workPlanning.materialsPdfBulkPending', {
            progress: 0,
          }),
        });

        let data;
        try {
          data = await this.pollMaterialLabelsJob(jobId, notification);
        } finally {
          notification.close();
        }

        if (data.status === 'timeout') {
          this.$notify({
            showClose: true,
            type: 'info',
            message: this.$t('workPlanning.materialsPdfBulkStillRunning'),
          });
          return;
        }

        const { batches, samplesProcessed, failedBatches } = data.result || {};
        this.$notify({
          showClose: true,
          type: failedBatches > 0 ? 'warning' : 'success',
          message:
            failedBatches > 0
              ? this.$t('workPlanning.materialsPdfBulkWarning', {
                  failedBatches,
                  samplesProcessed,
                  batches,
                })
              : this.$t('workPlanning.materialsPdfBulkSuccess', {
                  samplesProcessed,
                  batches,
                }),
        });
      },
      pollMaterialLabelsJob(jobId, notification) {
        const POLL_INTERVAL_MS = 2000;
        const MAX_POLL_MS = 5 * 60 * 1000;
        const startedAt = Date.now();
        return new Promise((resolve, reject) => {
          const tick = async () => {
            try {
              const { data } = await workPlanningApi.materialsPdfBulkStatus(
                jobId
              );
              if (notification) {
                // eslint-disable-next-line no-param-reassign
                notification.message = this.$t(
                  'workPlanning.materialsPdfBulkPending',
                  { progress: data.progress || 0 }
                );
              }
              if (data.status === 'completed') {
                resolve(data);
              } else if (data.status === 'failed') {
                reject(
                  new Error(
                    data.error || this.$t('workPlanning.materialsPdfBulkFailed')
                  )
                );
              } else if (Date.now() - startedAt > MAX_POLL_MS) {
                // Stop polling but don't treat as a hard failure — the job may
                // still finish on the printer side.
                resolve({ status: 'timeout' });
              } else {
                this.pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
              }
            } catch (error) {
              reject(error);
            }
          };
          tick();
        });
      },
      // Work program generation runs as an async backend job: enqueue, poll the
      // status (updating the spinner with the progress %), then download the
      // finished pdf. Returns an axios-like response so it flows through the same
      // viewOrSaveAttachment path as the other (synchronous) print actions.
      async generateWorkProgram({ where, analysisCode }, loading) {
        const {
          data: { jobId },
        } = await workPlanningApi.workProgramPdfEnqueue({
          where,
          analysisCode,
        });
        await this.pollWorkProgramJob(jobId, loading);
        // The job is done but the (potentially large) pdf still has to be pulled
        // down — without this the spinner sits at 100% and looks frozen.
        if (loading) loading.setText(this.$t('workPlanning.downloading'));
        return workPlanningApi.workProgramPdfDownload(jobId);
      },
      pollWorkProgramJob(jobId, loading) {
        const POLL_INTERVAL_MS = 1500;
        return new Promise((resolve, reject) => {
          const tick = async () => {
            try {
              const { data } = await workPlanningApi.workProgramPdfStatus(
                jobId
              );
              if (loading) {
                loading.setText(
                  this.$t('workPlanning.generatingProgress', {
                    progress: data.progress || 0,
                  })
                );
              }
              if (data.status === 'completed') {
                resolve(data);
              } else if (data.status === 'failed') {
                reject(
                  new Error(
                    data.error || this.$t('workPlanning.generationFailed')
                  )
                );
              } else {
                setTimeout(tick, POLL_INTERVAL_MS);
              }
            } catch (error) {
              reject(error);
            }
          };
          tick();
        });
      },
      throwError(error) {
        const message =
          error?.response?.data?.error?.message ||
          error?.message ||
          this.$t('workPlanning.generationFailed');
        this.$notify({
          showClose: true,
          type: 'error',
          message,
        });
      },
      viewOrSaveAttachment(blob) {
        const canBePreviewed = blob.type === 'application/pdf';
        const fileExtension = blob.type === 'application/pdf' ? '.pdf' : '.zip';
        if (!canBePreviewed) {
          fileSaver.saveAs(blob, `work-planning${fileExtension}`);
        } else {
          if (this.formData.printType === 'previewOnly') {
            window.open(URL.createObjectURL(blob));
          }
          if (
            this.formData.printType === 'printPdf' &&
            this.formData.printAction.some(
              (p) => p === 'printAttachment' || p === 'workProgram'
            )
          ) {
            printJS(URL.createObjectURL(blob));
          }
          if (this.formData.printType === 'generatePdf') {
            fileSaver.saveAs(blob, `work-planning${fileExtension}`);
          }
        }
      },
    },
    created() {
      this.formData = Object.keys(this.forms.workPlanning).length
        ? { ...this.forms.workPlanning }
        : { ...initialFormData };
    },
  };
</script>

<style lang="scss">
  .work-planning-form {
    &__radio {
      margin-bottom: 1rem;
    }
    &__bulk-warning {
      color: #e91b1b;
      font-size: 12px;
      margin-top: 6px;
    }
  }
</style>
